import {
  isContactCenterLiveAgentConfig,
  type LiveAgentConfig,
} from './get-channels-from-pypes';
import type { PlatformConnection } from './get-pype-connections';
import { CONTACT_CENTER_INTEGRATION_NAME } from './platform-catalog';
import { toIntegrationConfig } from './pype-agent-routing';
import type { ContactCenterIntegrationInfo } from './voice-handoff-types';

/**
 * Wiring a Voice AI channel to Contact Center, as Engagement actually reads it.
 *
 * Two things have to be true, and they are set in two different places:
 *
 * 1. the channel's PYPE carries `agent_routing_config.integration_name` —
 *    Engagement sends it as `X-Pypestream-Integration` and Middleware resolves
 *    the integration's callback URL by that name. **That is not this form's
 *    job**: it is written by binding the pype to a `PypeConnection` in Project
 *    Settings → Live Agent Platforms, and a pype is shared by every channel
 *    under it, so a voice channel must not rewrite it. The voice form only
 *    REPORTS whether it is there.
 * 2. the CHANNEL carries `metadata_integration_info_b64` — base64 JSON that
 *    Engagement decodes and reads `account_id` / `voice_inbox_id` / `inbox_id`
 *    out of when a `voice_inbox` handoff fires
 *    (`VoiceInboxHandoff.resolve_account_id/2` → `id_value/3` falls back to the
 *    decoded blob when the routing rule carries no ids). That write is what
 *    this module builds.
 *
 * Without (1) Middleware answers 401 — the integration cannot be resolved by
 * name. Without (2) Engagement logs
 * `TWILIO_VOICE_INBOX_HANDOFF_MISSING_ACCOUNT_ID` and ends the call. Neither
 * failure is visible in Pro Studio, which is why the form reports on both.
 */

/**
 * Fold an integration name for COMPARISON only — never for storage.
 *
 * Separators and case both go: the name is typed by hand on the platform side,
 * and `ps-select-option` rewrites a space in a `value` to `_`, so the same
 * platform can come back spelled `Contact Center`, `contact_center` or
 * `ContactCenter` depending on which way it travelled.
 */
const foldIntegrationName = (name?: string | null): string =>
  (name ?? '').replace(/[\s_-]+/g, '').toLowerCase();

const CONTACT_CENTER_FOLDED = foldIntegrationName(
  CONTACT_CENTER_INTEGRATION_NAME
);

/**
 * Whether a connection is the external Contact Center connector.
 *
 * Deliberately NOT `isContactCenterPlatform` from `platform-catalog`, even
 * though the spellings overlap. That predicate answers a different question
 * about a different field — a PYPE's `agent_routing` — and gates the Default
 * Messages copy; widening or narrowing it for this caller would silently move
 * that one. This one reads a
 * connection's `platformId`, which is the integration NAME as the catalog
 * reported it.
 *
 * The internal `cc` / `contact_center` schemas are excluded on purpose: they are
 * the prime-native routing, carry no `account_id` / `inbox_id` fields, and are a
 * different platform from the Middleware-registered `ContactCenter` integration.
 */
export const isContactCenterConnection = (
  connection?: Pick<PlatformConnection, 'platformId'> | null
): boolean =>
  foldIntegrationName(connection?.platformId) === CONTACT_CENTER_FOLDED;

/**
 * The channel-side integration info for a Contact Center connection, or `null`
 * when the connection cannot identify an inbox.
 *
 * `null` is a real answer, not a failure to compute: the ids live on the
 * connection (`settings.integration_headers`), and a connection saved without
 * them would otherwise produce a blob Engagement decodes to nothing — which it
 * reads as absent anyway, but only once a call is already in flight. Returning
 * `null` lets the form refuse the save instead.
 *
 * Read through `toIntegrationConfig` rather than off `settings` directly: the
 * same ids appear nested under `integration_headers` or flat at the top level
 * depending on who wrote the config, and that helper already knows both.
 */
export const toContactCenterIntegrationInfo = (
  connection?: Pick<PlatformConnection, 'platformId' | 'settings'> | null
): ContactCenterIntegrationInfo | null => {
  if (!connection || !isContactCenterConnection(connection)) {
    return null;
  }

  const { integration_headers: headers } = toIntegrationConfig(
    connection.settings
  );
  const accountId = headers.account_id.trim();
  const inboxId = headers.inbox_id.trim();

  if (!accountId || !inboxId) {
    return null;
  }

  return {
    // The ONE canonical spelling, stamped rather than echoed back from the
    // connection: a hand-typed `contact_center` on the platform side must not
    // reach the wire, where Engagement and the guards compare it.
    integrator: CONTACT_CENTER_INTEGRATION_NAME,
    account_id: accountId,
    inbox_id: inboxId,
  };
};

/**
 * The ids a channel already stores, or `null` when it stores no Contact Center
 * blob.
 *
 * The guard that decides which platform the blob belongs to lives beside the
 * shapes it discriminates (`isContactCenterLiveAgentConfig` in
 * `get-channels-from-pypes`), not here — one home for "what can be in this
 * slot" keeps the two arms and the test that tells them apart from drifting.
 * It NARROWS, which is what lets the two ids be read below at all.
 *
 * `null` for a half-written blob: one id names no inbox, and returning it would
 * let the picker claim a selection the channel cannot route with. Trimmed,
 * because what reaches here came off the wire.
 */
export const toStoredIntegrationInfo = (
  config?: LiveAgentConfig | null
): ContactCenterIntegrationInfo | null => {
  if (!isContactCenterLiveAgentConfig(config)) {
    return null;
  }
  const accountId = (config.account_id ?? '').trim();
  // `voice_inbox_id` is the earlier Engagement spelling and may be what a blob
  // written against that contract stores; Engagement reads either.
  const inboxId = (config.inbox_id ?? config.voice_inbox_id ?? '').trim();
  return accountId && inboxId
    ? {
        integrator: CONTACT_CENTER_INTEGRATION_NAME,
        account_id: accountId,
        inbox_id: inboxId,
      }
    : null;
};

/**
 * Which project connection a channel's stored ids came from.
 *
 * Matched on the ids rather than on a stored connection id, because the blob
 * holds no connection id to store — and that is fine: two connections with the
 * same account and inbox address the same Contact Center inbox, so picking
 * either is correct. `undefined` means the channel points at an inbox no
 * current connection describes (the connection was deleted, or it belongs to
 * the other environment), which the form reports rather than silently
 * reselecting something else.
 */
export const findConnectionForIntegrationInfo = (
  connections: readonly PlatformConnection[],
  info: ContactCenterIntegrationInfo | null
): PlatformConnection | undefined => {
  if (!info) {
    return undefined;
  }
  return connections.find((connection) => {
    const candidate = toContactCenterIntegrationInfo(connection);
    return (
      candidate?.account_id === info.account_id &&
      candidate?.inbox_id === info.inbox_id
    );
  });
};

/**
 * Which integration info a save should write onto the channel.
 *
 * Extracted from the form because it is the decision that silently loses data
 * when it is wrong, and inside the component no test can reach it — the form
 * imports design-system Lit elements, which crash this package's jsdom
 * environment (the same reason `buildLiveAgentPlatformPayload` lives outside the
 * connector modal).
 *
 * **An untouched picker keeps what the channel already stores.** The selection
 * is resolved by matching the stored ids against the project's connections, and
 * that match legitimately fails while connections are still loading, when the
 * load failed, when the connection was deleted or its ids were edited, and for a
 * legacy blob. In all of those `selectedInfo` is `null` — and because the
 * payload rebuilds the whole map, returning `null` there is not "no change", it
 * is an explicit erase of the ids. So while the user has not touched the picker,
 * the stored value wins.
 *
 * **A touched picker is taken literally**, including `''` (None): that is the
 * one case where clearing the ids is what was asked for.
 */
export const resolveIntegrationInfoForSave = ({
  pickedConnectionId,
  selectedInfo,
  storedInfo,
}: {
  /** `null` while the user has not touched the picker; a string once they have. */
  pickedConnectionId: string | null;
  /** Info for the connection the picker currently resolves to. */
  selectedInfo: ContactCenterIntegrationInfo | null;
  /** Info the channel already stores, decoded from its own config. */
  storedInfo: ContactCenterIntegrationInfo | null;
}): ContactCenterIntegrationInfo | null =>
  pickedConnectionId === null ? (selectedInfo ?? storedInfo) : selectedInfo;
